/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { FormDefinitionWrapper } from '../../react/components/form/services/form';
import { DBSchema, OpenDBCallbacks } from "idb";
import { Evaluation } from "../../evaluation";
import { DairyFARMDB } from "./../store";
import { FORMS_STORE } from "./forms";

export const EVALS_STORE = "evals" as "evals";
export const EVALS_TO_CREATE_STORE = "evals_to_create" as "evals_to_create";

export interface CreateEvalRecord {
  id: string;
  formId: string;
  formUuid: string;
  author: string;
}

export interface EvalStore extends DBSchema {
  [EVALS_STORE]: {
    key: string;
    value: Evaluation;
    indexes: {
      facility: string;
      author: string;
      uuid: string;
      modified: string;
    };
  };
  [EVALS_TO_CREATE_STORE]: {
    key: string;
    value: CreateEvalRecord;
  };
}

export const evalStoreConfig: OpenDBCallbacks<DairyFARMDB> = {
  upgrade(db, oldVersion, newVersion, transaction) {
    // Using switch to incrementally adjust db. No breaks so that it cascades through each 'migration'
    switch (oldVersion) {
      // Cause db versions 1-7 to take the initial construct branch rather than rework it for safety
      case (oldVersion < 8 ? oldVersion : -1):  
        if (!db.objectStoreNames.contains(EVALS_STORE)) {
          const evalStore = db.createObjectStore(EVALS_STORE, {
            keyPath: "id",
          });
          evalStore.createIndex("author", "author");
          evalStore.createIndex("facility", "facility.id");
          evalStore.createIndex("uuid", "uuid");
        } else if (!transaction.objectStore(EVALS_STORE).index("uuid")) {
          transaction.objectStore(EVALS_STORE).createIndex("uuid", "uuid");
        }
        //@ts-ignore no-fallthrough
        case 8:
          transaction.objectStore(EVALS_STORE).createIndex("modified", "modified");
          //@ts-ignore no-fallthrough
      default:
        break;
    }

    if (!db.objectStoreNames.contains(EVALS_TO_CREATE_STORE)) {
      db.createObjectStore(EVALS_TO_CREATE_STORE, {
        keyPath: "id",
      });
    }

    if (oldVersion < 8) {
      // memoized cache of forms to avoid unnecessary repeated iterations over form store
      const cForms: { [key: string]: FormDefinitionWrapper } = {};
      transaction
        .objectStore(EVALS_TO_CREATE_STORE)
        .openCursor()
        .then(function updateRecord(cursor): Promise<void> {
          if (cursor === null) return Promise.resolve();

          const record = cursor.value;

          // Load the latest available form definition for the type created
          const resolveForm =
            cForms[record.formId] !== undefined
              ? Promise.resolve(cForms[record.formId])
              : transaction
                  .objectStore(FORMS_STORE)
                  .index("type")
                  .getAll(record.formId)
                  .then(function loadRespectiveForm(forms: FormDefinitionWrapper[]) {
                    // Order by major then minor versions descending
                    const sortedForms = forms.sort((a, b) =>
                      b.version_major === a.version_major
                        ? b.version_minor - a.version_minor
                        : b.version_major - a.version_major
                    );

                    const iTargetForm = sortedForms.findIndex(form => form.state !== "retired"); // Get first valid form
                    const targetForm = iTargetForm >= 0 ? sortedForms[iTargetForm] : sortedForms[0]; // fall back to latest retired

                    cForms[record.formId] = targetForm;
                    return targetForm;
                  });
          return resolveForm
            .then(form => {
              cursor.update({
                ...record,
                formUuid: form.id,
              });
            })
            .then(() => cursor.continue())
            .then(updateRecord);
        });
    }
  },
};
